import posthog, { type Survey } from 'posthog-js'

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || 'https://eu.i.posthog.com'
const POSTHOG_ENV_ENABLED = (import.meta.env.VITE_POSTHOG_ENABLED as string | undefined) === 'true'
let posthogEnabled = POSTHOG_ENV_ENABLED
let initialized = false

export function configureAnalytics(enabledOverride: boolean | null | undefined): void {
  posthogEnabled = typeof enabledOverride === 'boolean' ? enabledOverride : POSTHOG_ENV_ENABLED

  if (!initialized) return
  if (!posthogEnabled) {
    posthog.stopSessionRecording?.()
    posthog.opt_out_capturing()
  } else if (hasStatisticsConsent()) {
    posthog.opt_in_capturing()
    applyKillSwitch()
  }
}

/**
 * Remote kill switch. Create a boolean feature flag with this exact key in
 * PostHog and roll it out to 100% to disable ALL analytics (events, autocapture
 * and session replay) on every browser, with no code deploy. Flip it back off
 * to re-enable. Propagation is a page reload / a few minutes.
 */
const KILL_SWITCH_FLAG = 'analytics-kill-switch'

function applyKillSwitch(): void {
  if (posthog.isFeatureEnabled(KILL_SWITCH_FLAG)) {
    posthog.stopSessionRecording?.()
    posthog.opt_out_capturing()
  } else if (posthog.has_opted_out_capturing() && hasStatisticsConsent()) {
    // switch was turned back off and the user still consents → resume
    posthog.opt_in_capturing()
  }
}

export function hasStatisticsConsent(): boolean {
  if (localStorage.getItem('cookies-accepted') !== 'true') return false
  try {
    const prefs = JSON.parse(localStorage.getItem('preferences') ?? '{}')
    return prefs.statistics === true
  } catch {
    return false
  }
}

// PostHog is initialized only when enabled and after the user grants statistics
// consent. Automatic capture and session replay are enabled for opted-in users.
function initPosthog(): void {
  if (!posthogEnabled || initialized || !POSTHOG_KEY) return
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,
    persistence: 'localStorage+cookie',
    // Only create a person profile once we identify the user (after login / an
    // enquiry). Anonymous visitors still send events but don't burn the paid
    // person-profile allowance.
    person_profiles: 'identified_only',
    // --- Session Replay (#3) --------------------------------------------------
    // Records a video-like replay of real sessions. Off until the user consents
    // to statistics; still needs "Record user sessions" enabled in the PostHog
    // project settings. Masking is deliberately NOT configured here: any
    // masking option set in code overrides the project's privacy settings, so
    // what gets masked is controlled from the PostHog console instead (password
    // inputs stay masked by the SDK regardless).
    // Capture automatic interactions and session replays only after consent.
    disable_session_recording: false,
    autocapture: true,
    capture_dead_clicks: true,
    loaded: () => applyKillSwitch()
  })
  // Re-evaluate whenever flags refresh, so flipping the switch in PostHog takes
  // effect without waiting for a fresh page load.
  posthog.onFeatureFlags(() => applyKillSwitch())
  initialized = true
}

/** Starts capturing if consent was already given (e.g. on app boot). No-op otherwise. */
export function initAnalyticsIfConsented(): void {
  if (hasStatisticsConsent()) initPosthog()
}

/** Called when the user grants statistics consent (accept all / accept selected with statistics on). */
export function enableAnalytics(): void {
  if (!posthogEnabled || !POSTHOG_KEY) return
  if (!initialized) {
    initPosthog()
  } else {
    posthog.opt_in_capturing()
    applyKillSwitch()
  }
}

/** Called when the user withholds/withdraws statistics consent. */
export function disableAnalytics(): void {
  if (!initialized) return
  posthog.opt_out_capturing()
}

function canCapture(): boolean {
  return posthogEnabled && initialized && !posthog.has_opted_out_capturing()
}

export function trackPageview(): void {
  if (!canCapture()) return
  posthog.capture('$pageview')
}

/**
 * Names of the product events we emit by hand. Keeping them here avoids typos
 * scattering slightly different strings across the funnel (#2).
 */
export type AnalyticsEvent =
  | 'registration_submitted'
  | 'enquiry_form_submitted'
  | 'enquiry_updated'
  | 'enquiry_withdrawn'
  | 'offer_viewed'
  | 'offer_accepted'
  | 'offer_declined'
  | 'cancellation_requested'
  | 'booking_extended'
  | 'invoice_viewed'

export function track(event: AnalyticsEvent, props?: Record<string, unknown>): void {
  if (!canCapture()) return
  posthog.capture(event, props)
}

/**
 * Reports a caught (already-handled) error to PostHog Error Tracking, so
 * failures we swallow with a console.error/alert still show up somewhere —
 * useful for intermittent bugs (CSRF/session expiry, flaky network) that
 * can't be reproduced on demand.
 */
export function captureException(error: unknown, additionalProperties?: Record<string, unknown>): void {
  if (!canCapture()) return
  posthog.captureException(error, additionalProperties)
}

export function identifyUser(id: string | number, props?: Record<string, unknown>): void {
  if (!canCapture()) return
  posthog.identify(String(id), props)
}

/**
 * Group analytics (#1). Attaches every subsequent event to a group so PostHog can
 * report conversion / drop-off per company and per enquiry, not just per user.
 * `type` must match a group type enabled in the PostHog project settings.
 */
export function setGroup(type: 'company' | 'enquiry', key: string | number, props?: Record<string, unknown>): void {
  if (!canCapture()) return
  posthog.group(type, String(key), props)
}

export function resetAnalyticsIdentity(): void {
  if (!initialized) return
  posthog.reset()
}

/**
 * Surveys are created in PostHog as "API" surveys: PostHog stores the questions
 * and the answers, the portal decides when to ask and renders them itself
 * (FeatureSurveyHost). Resolves null without consent or when no running survey
 * has this name, so callers simply skip asking.
 *
 * getActiveMatchingSurveys only returns surveys this person may still see:
 * PostHog's per-survey targeting flag drops anyone who already answered or
 * dismissed it (on any device, via the identified person), and the SDK's
 * seenSurvey_<id> localStorage key covers this browser until flags reload.
 */
const SURVEYS_LOAD_TIMEOUT_MS = 10_000

export function getSurveyByName(name: string): Promise<Survey | null> {
  if (!canCapture()) return Promise.resolve(null)

  return new Promise(resolve => {
    let settled = false

    const finish = (survey: Survey | null) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeout)
      // Deferred: onSurveysLoaded may call back synchronously, before `unsubscribe` exists.
      queueMicrotask(() => unsubscribe())
      resolve(survey)
    }

    // getActiveMatchingSurveys never calls back before the surveys extension
    // has loaded, so wait for it — and give up rather than hang forever.
    const timeout = window.setTimeout(() => finish(null), SURVEYS_LOAD_TIMEOUT_MS)
    const unsubscribe = posthog.onSurveysLoaded((_surveys, context) => {
      if (!context?.isLoaded) return finish(null)
      posthog.getActiveMatchingSurveys(surveys => finish(surveys.find(survey => survey.name === name) ?? null))
    })
  })
}

export type SurveyEvent = 'survey shown' | 'survey sent' | 'survey dismissed'

/** `responses` maps a PostHog question id to the answer (numbers as strings, as PostHog expects). */
export function captureSurveyEvent(event: SurveyEvent, surveyId: string, responses: Record<string, string> = {}): void {
  if (!canCapture()) return

  const responseProperties = Object.fromEntries(
    Object.entries(responses).map(([questionId, value]) => [`$survey_response_${questionId}`, value])
  )
  posthog.capture(event, { $survey_id: surveyId, ...responseProperties })
}
